if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('.product-form__submit');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;

        if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        // TP Addition: Prevent submission if the form is invalid
        const isValid = document.querySelector('tp-variant-validation')?.validate();
        if (this.dataset.tpValidate === 'true' && isValid === false) {
          return;
        }

        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
        }
        const loadingSpinnerEl = this.querySelector('.loading__spinner');
        if (loadingSpinnerEl) loadingSpinnerEl.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // TP-Adjusment 1079: Handle test racket multi-product submission
        const isTestRacket = this.classList.contains('tp-js-test-racket-form');
        if (isTestRacket) {
          const desiredQuantity = parseInt(formData.get('quantity')) || 1;

          const currentCart = await window.cartApi.getCart();

          const testRacketsInCartCount = currentCart.items.reduce((count, item) => {
            if (item.properties && item.properties["_bundle-type"] === 'test-racket' && item.properties["_main-product-bundle"] === 'true') {
              return count + item.quantity;
            }
            return count;
          }, 0);

          // It's possible to add a maximum of `window.DEPT.shopMetafields.testRacketMaxCount` test
          // rackets in total.
          // Adjust the quantity to add accordingly. If the adjusted quantity is 0, block the
          // addition - else add the adjusted quantity.

          const { testRacketMaxCount } = window.DEPT.shopMetafields;
          const { cartValidationStrings } = window.DEPT;

          const additionalTestRacketsAllowed = testRacketMaxCount - testRacketsInCartCount;

          const adjustedQuantity = Math.min(desiredQuantity, additionalTestRacketsAllowed);

          if (adjustedQuantity === 0) {
            this.handleErrorMessage(`${cartValidationStrings.testRacketsLimitReachedErrorTemplate.replace('[limit]', testRacketMaxCount)}`);
            if (this.submitButton) {
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('aria-disabled');
            }
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            return;
          }

          if (adjustedQuantity < desiredQuantity) {
            this.handleErrorMessage(`${cartValidationStrings.testRacketsPartiallyAddedErrorTemplate.replace('[adjusted_quantity]', adjustedQuantity)}`);
          }

          this.handleTestRacketSubmission({config, quantity: adjustedQuantity, formSubmitEvent: evt});
          return;
        }
        // END TP-Adjusment 1079

        config.body = formData;
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;
              if (!soldOutMessage) return;
              if (this.submitButton) this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  quantity: parseInt(formData.get('quantity')) || 1,
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(response);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              }

              // TP-Adjustment (TPSR-127): Handle alternative color modal close
              const alternativeColorModal = this.closest('tp-modal[id^="tp-modal-available-siblings"');
              if (alternativeColorModal) {
                alternativeColorModal.close();
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.submitButton) this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            
            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper') || this.closest(".tp-quantity-input-and-buy-buttons").querySelector(".tp-product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      // TP-Adjusment 1079: Handle test racket submission with multiple products
      handleTestRacketSubmission({config, quantity, formSubmitEvent}) {
        const variantInputElements = this.form.querySelectorAll('.tp-js-test-racket-product-variant-id');
        let mainTestRacketSku = null;
        let mainProductIndex = 0;

        const productData = [...variantInputElements].map((variantInputElement, index) => {
          const variantId = variantInputElement.value;
          const isMainProduct = variantInputElement.hasAttribute('data-tp-sku');

          if (isMainProduct) {
            mainTestRacketSku = variantInputElement.getAttribute('data-tp-sku');
            mainProductIndex = index;
          }

          return {
            variantId: Number(variantId),
            quantity,
          };
        })

        const bundleId = window.DEPT.generateBundleId(mainTestRacketSku || 'test-racket');
        const bundleProperties = {
          bundleType: "test-racket",
          bundleId,
        };

        const items = window.DEPT.createBundleCartItems(productData, bundleProperties, mainProductIndex);

        const body = {
          // Reverse items array to get the main test racket first in cart
          items: items.reverse()
        };

        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: items[0]?.id,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;
              if (!soldOutMessage) return;
              if (this.submitButton) this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: items[0]?.id,
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(response);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.submitButton) this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", formSubmitEvent);
          });
      }
    }
  );
}
